<template>
  <v-container no-gutters fluid class="fill-height pa-0">
    <v-row no-gutters class="flex-column fill-height flex-nowrap" style="max-width: 100%" v-if="isFetched">
      <v-col cols="auto" style="max-width: 100%;" class="elevation-4 panel--top">
        <v-row no-gutters class="justify-center align-center pt-1 pb-2 fill-height">
          <v-col cols="10">
            <v-row no-gutters class="flex-nowrap">
              <v-col cols="auto" class="px-2 d-flex align-center" style="height: auto;">
                <img ref="logo" @click="goToStart" style="max-height: 55px; cursor: pointer" :src="url" contain />
              </v-col>
              <v-col>
                <v-row no-gutters>
                  <v-col cols="auto">
                    <v-subheader
                      class="pa-0 text-h6 font-weight-regular font--text"
                      style="height: auto; font-size: 18px !important;"
                      v-text="$i18n.t('navbar.admin')"
                    ></v-subheader>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="10">
                    <v-subheader
                      class="pa-0 text-subtitle-2 font-weight-regular text-truncate d-block text-left"
                      style="height: auto"
                      v-text="appName"
                    ></v-subheader>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" class="mr-2">
            <dots-menu :items="buttonTools" @map="openMapView" @logout="logoutHandler" is-absolute>
              <template #activator="{ on }">
                <base-rounded-button icon="mdi-account" size="x-small" v-on="on" :tooltip="currentUserName">
                </base-rounded-button>
              </template>
            </dots-menu>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="auto" style="max-width: 100%;" class="fill-height">
        <v-row no-gutters class="fill-height">
          <v-col cols="auto" class="elevation-3" style="z-index: 3">
            <v-navigation-drawer permanent clipped width="250">
              <list-expansion-panels :items="filteredMenus"></list-expansion-panels>
            </v-navigation-drawer>
          </v-col>
          <v-col
            class="router__wrapper"
            ref="adminWrapper"
            :style="`${iframes.includes(routeName) ? 'padding:0' : ''}`"
          >
            <router-view></router-view>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <main-loader v-else></main-loader>
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash';
import { get, call, sync } from 'vuex-pathify';

import logo from '@/mixins/logo';
import menusAdmin from '@/assets/js/menusAdmin';

import DotsMenu from '@/components/DotsMenu';
import ListExpansionPanels from '@/components/ListExpansionPanels';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'Admin',
  mixins: [logo],
  components: {
    DotsMenu,
    MainLoader,
    ListExpansionPanels,
  },
  data: () => ({
    development: process.env.NODE_ENV !== 'production',
    iframes: ['geoserver'],
    isFetched: false,
    url: `${process.env.VUE_APP_API_URL}/settings/logo`,
    buttonTools: [
      {
        name: 'map',
      },
      {
        name: 'logout',
      },
    ],
    menus: menusAdmin,
    offsetHeight: undefined,
    scrollHeight: undefined,
  }),
  computed: {
    isAdminContentOverflowing: sync('tools/isAdminContentOverflowing'),
    appName: get('admin/settingsValues@app_name'),
    tokenUke: get('authentication/tokenUke'),
    redirectLogoutUrl: get('authentication/redirectLogoutUrl'),
    isSuperadmin: get('users/currentUser@is_superadmin'),
    currentUserName: get('users/currentUser@name'),
    currentTab: sync('sidebar/currentTab'),
    additionalModules: get('admin/additionalModules'),
    routeName() {
      return this.$route.name;
    },
    filteredMenus() {
      return this.$_filterNestedArray(cloneDeep(this.menus), this.filterMenu, 'items').map(items => {
        if (items.items) {
          items.items = items.items.map(item => {
            const verboseName = item.customNamePath
              ? this.$i18n.t(item.customNamePath)
              : '' || item.enableModuleName?.[0]
              ? this.getModuleVerboseName(item.enableModuleName)
              : '' || this.$i18n.t(`navbar.${item?.name}`);
            return { ...item, verboseName };
          });
          items.items.sort((a, b) => {
            return a.verboseName.localeCompare(b.verboseName);
          });
        }
        return items;
      });
    },
    isOverflowing() {
      return this.scrollHeight > this.offsetHeight;
    },
  },
  methods: {
    getDatabaseSrid: call('map/getDatabaseSrid'),
    getLayers: call('layers/getLayers'),
    logout: call('authentication/logout'),
    getSettings: call('admin/getSettings'),
    getAdditionalModules: call('admin/getAdditionalModules'),
    async logoutHandler() {
      await this.logout();
      if (this.tokenUke && this.redirectLogoutUrl) {
        window.location.href = this.redirectLogoutUrl;
      } else {
        this.$router.push({ name: 'login' });
        window.location.reload();
      }
    },
    openMapView() {
      this.currentTab = 0;
      this.$router.push({ name: 'map' });
    },
    filterMenu(menu) {
      if (menu?.name === 'superadmin') {
        return this.isSuperadmin;
      } else if (menu?.name === 'devTab') {
        return this.isSuperadmin || this.development;
      } else if (!menu.isAuthorized) {
        return true;
      } else {
        const { oneModuleRequired = false } = menu;
        return oneModuleRequired
          ? menu.enableModuleName.find(name => this.additionalModules[name]?.enabled) || false
          : menu.enableModuleName.every(name => this.additionalModules[name]?.enabled);
      }
    },
    goToStart() {
      this.$router.push({ name: 'cockpit' });
    },
    async init() {
      try {
        await Promise.all([this.getLayers(), this.getSettings(), this.getDatabaseSrid(), this.getAdditionalModules()]);
        document.title = this.appName;
        setTimeout(() => {
          this.isFetched = true;
        }, 1000);
      } catch (e) {
        console.log(e);
        this.isFetched = false;
      }
    },
    getModuleVerboseName(moduleName) {
      return this.additionalModules[moduleName[0]]?.verbose_name;
    },
  },
  watch: {
    isOverflowing(nV) {
      this.isAdminContentOverflowing = nV;
    },
  },
  created() {
    this.init();
    this.$once('hook:beforeDestroy', () => {
      this.$off();
    });
  },
  mounted() {
    this.$root.$emit('socket.close', { name: 'default' });
    setInterval(() => {
      this.scrollHeight = this.$refs.adminWrapper?.scrollHeight;
      this.offsetHeight = this.$refs.adminWrapper?.offsetHeight;
    }, 50);
  },
};
</script>

<style lang="scss" scoped>
.panel--top {
  position: relative;
  z-index: 5;
}
.router__wrapper {
  max-height: calc(100vh - 70px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 34px 60px !important;
}
</style>
